/**
 * Type definitions for the admin module management system
 */

// Common interface for both types of modules
export interface BaseModule {
  module_id: string;
  module_version: number;
  module_name: string;
  generation_prompt: string;
  created_on: number; // Unix timestamp (float)
  modified_on: number; // Unix timestamp (float)
  models?: string[];
  temperature?: number;
  max_tokens?: number;
  meta_module: boolean;
  markdown: boolean;
  module_type?: 'public' | 'user'; // Added to help with type identification when referenced from templates
}

// ExtractionPrompts type matches the Python model
export interface ExtractionPrompts {
  semantic_prompt?: string;
  keyword_prompt?: string;
}

// Represents a public module (from Python Module class)
export interface PublicModule extends BaseModule {
  extraction_prompts?: ExtractionPrompts;
  update_prompt?: string;
  placeholders?: string[];
  public?: boolean;
  public_name?: string;
  public_description?: string;
  public_categories?: string[];
  public_example?: string;
  _moduleType: 'public'; // Used to distinguish module types
}

// Represents a user module (from Python UserModule class)
export interface UserModuleAdmin extends BaseModule {
  extraction_prompts?: ExtractionPrompts;
  created_by?: string;
  copied_from?: string;
  parent_module?: string;
  language?: string;
  user_module_description?: string;
  user_example?: string;
  deactivated?: boolean;
  public_name?: string;
  public_description?: string;
  _moduleType: 'user'; // Used to distinguish module types
}

// Union type for any module
export type AnyModule = PublicModule | UserModuleAdmin;

// API request/response types
export interface ModuleListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterByType?: 'public' | 'user';
  filterByIsPublic?: boolean;
  filterByMetaModule?: boolean;
  filterByMarkdown?: boolean;
  filterByLanguage?: string;
  filterByDeactivated?: boolean;
  filterByUserId?: string;
  filterByModuleId?: string;
}

export interface PaginatedModulesResponse {
  data: AnyModule[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateModulePayload {
  _moduleType: 'public' | 'user';
  module_name: string;
  generation_prompt: string;
  extraction_prompts?: ExtractionPrompts;
  update_prompt?: string;
  placeholders?: string[];
  models?: string[];
  temperature?: number;
  max_tokens?: number;
  public?: boolean;
  public_name?: string;
  public_description?: string;
  public_categories?: string[];
  public_example?: string;
  meta_module?: boolean;
  markdown?: boolean;
  // User module specific fields
  language?: string;
  user_module_description?: string;
  user_example?: string;
}

export interface UpdateModulePayload {
  _moduleType: 'public' | 'user';
  module_name?: string;
  generation_prompt?: string;
  extraction_prompts?: ExtractionPrompts;
  update_prompt?: string;
  placeholders?: string[];
  models?: string[];
  temperature?: number;
  max_tokens?: number;
  public?: boolean;
  public_name?: string;
  public_description?: string;
  public_categories?: string[];
  public_example?: string;
  meta_module?: boolean;
  markdown?: boolean;
  // User module specific fields
  language?: string;
  user_module_description?: string;
  user_example?: string;
  deactivated?: boolean;
}

// Model names mapping used in UI dropdowns/selects
export const MODEL_NAMES: Record<string, string> = {
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5-nano': 'GPT-5 Nano',
  'gpt-5': 'GPT-5',
  'gpt-4o': 'GPT-4o',
  'claude-3-5-sonnet': 'Claude 3.5 Sonnet',
  'gpt-4': 'GPT-4',
  'gpt-3.5-turbo': 'GPT-3.5-Turbo',
  'gpt-4-turbo': 'GPT-4-Turbo (OpenAI)',
  'azure-gpt-4-turbo': 'GPT-4-Turbo (Azure)',
  'claude-3-opus': 'Claude 3 Opus',
  'claude-3-sonnet': 'Claude 3 Sonnet',
};
